import type { Convenio } from "@/api/contrato";
import {
  TAMANHO_DA_PAGINA,
  useBloquearConvenio,
  usePaginaDeConvenios,
} from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { CampoDeBusca } from "@/componentes/CampoDeBusca";
import { ChipsDeFiltro } from "@/componentes/ChipsDeFiltro";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { MenuDeLinha, type ItemDoMenu } from "@/componentes/MenuDeLinha";
import { Paginacao } from "@/componentes/Paginacao";
import { PilulaDeExcecao, type Matiz } from "@/componentes/PilulaDeSituacao";
import { Selecao } from "@/componentes/Selecao";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { useAlternarSituacao } from "@/componentes/useAlternarSituacao";
import {
  useErroDaPagina,
  useFiltrosNaUrl,
  type DefinicaoDeFiltro,
} from "@/componentes/useFiltrosNaUrl";
import { banco } from "@/formato";
import { rotaDoCedente, rotaDoConvenio } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { FiltroDeBanco } from "./ListaDeCedentes";
import { textoDaRecusa } from "./recusas";

/** O enum do contrato. Valor novo aparece cru, e não some da varredura. */
const SITUACOES: Readonly<Record<string, { rotulo: string; matiz: Matiz }>> = {
  ativo: { rotulo: "Ativo", matiz: "pago" },
  bloqueado: { rotulo: "Bloqueado", matiz: "falha" },
  provisionando: { rotulo: "Provisionando", matiz: "transito" },
  pendente_credencial: { rotulo: "Pendente de credencial", matiz: "transito" },
};

const MODOS_DE_REGISTRO: Readonly<Record<string, string>> = {
  manual: "Manual",
  automatico_van: "Automático VAN",
  webservice: "WebService",
  misto: "Misto",
};

/** O filtro dos alertas da visão geral. Valor fora do enum é ignorado: a API recusaria com `400`. */
const SITUACOES_DO_CERTIFICADO: Readonly<Record<string, string>> = {
  vencido: "Vencido",
  vencendo: "Vencendo",
};

const FILTROS: readonly DefinicaoDeFiltro<"q" | "banco" | "status" | "certificado">[] = [
  { chave: "q", rotulo: "Busca" },
  { chave: "banco", rotulo: "Banco", textoDoValor: banco },
  {
    chave: "status",
    rotulo: "Situação",
    textoDoValor: (valor) => SITUACOES[valor]?.rotulo ?? valor,
  },
  {
    chave: "certificado",
    rotulo: "Certificado",
    textoDoValor: (valor) => SITUACOES_DO_CERTIFICADO[valor] ?? valor,
    aceita: (valor) => Object.hasOwn(SITUACOES_DO_CERTIFICADO, valor),
  },
];

const OPCOES_DE_SITUACAO: readonly (readonly [string, string])[] = [
  ["", "Todas"],
  ...Object.entries(SITUACOES).map(([valor, { rotulo }]) => [valor, rotulo] as const),
];

/** A página da lista, a partir da URL, como `useListaDeCedentes`. */
export function useListaDeConvenios() {
  const { cliente, estado } = useSessao();
  const filtros = useFiltrosNaUrl(FILTROS);
  const consulta = usePaginaDeConvenios(
    cliente,
    { ...filtros.valores, cursor: filtros.cursor, limite: TAMANHO_DA_PAGINA },
    estado.fase === "dentro",
  );

  return { filtros, consulta };
}

/*
 * A varredura de todos os cedentes: é aqui que o convênio bloqueado salta aos olhos, e por
 * isso `ativo` não tem pílula. A linha leva ao cedente dono, com o painel do convênio aberto.
 */
export function ListaDeConvenios() {
  const { filtros, consulta } = useListaDeConvenios();
  const { data, error, isPending } = consulta;
  const { erro, recuperando } = useErroDaPagina(error);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <CampoDeBusca
          rotulo="Buscar convênio"
          placeholder="Número, razão social ou documento"
          valor={filtros.valores.q}
          aoMudar={(valor) => filtros.ajustar("q", valor)}
        />
        <FiltroDeBanco
          valor={filtros.valores.banco}
          aoMudar={(valor) => filtros.ajustar("banco", valor)}
        />
        <Selecao
          rotulo="Situação"
          opcoes={OPCOES_DE_SITUACAO}
          value={filtros.valores.status ?? ""}
          onChange={(evento) => filtros.ajustar("status", evento.target.value)}
          className="h-8!"
        />
      </div>

      <ChipsDeFiltro filtros={filtros} />

      {erro !== null ? (
        <FaixaDeErro erro={erro} />
      ) : (
        <>
          <Tabela
            rotulo="Convênios"
            colunas={COLUNAS}
            itens={data?.convenios ?? []}
            carregando={isPending || recuperando}
            chaveDe={(convenio) => convenio.id}
            hrefDe={(convenio) => rotaDoConvenio(convenio.pessoa_id, convenio.id)}
            rotuloDe={(convenio) => `Convênio ${convenio.banco}/${convenio.numero}`}
            vazio="Nenhum convênio."
          />

          {data !== undefined && (
            <Paginacao
              itens={data.convenios.length}
              total={data.total}
              proximoCursor={data.proximo_cursor}
            />
          )}
        </>
      )}
    </div>
  );
}

function AcoesDoConvenio({ convenio }: { convenio: Convenio }) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const bloquear = useBloquearConvenio(cliente, convenio.id);
  const ehAdmin = pode("admin");
  const ligado = convenio.status !== "bloqueado";
  const nome = `${convenio.banco}/${convenio.numero}`;

  const situacao = useAlternarSituacao({
    ligado,
    desligar: {
      titulo: `Bloquear o convênio ${nome}?`,
      corpo: "Nenhum boleto deste convênio é aceito enquanto ele estiver bloqueado.",
      verbo: "Bloquear convênio",
    },
    ligar: {
      titulo: `Desbloquear o convênio ${nome}?`,
      corpo: "O convênio volta a aceitar boletos.",
      verbo: "Desbloquear convênio",
    },
    aoAlterar: async (ligar, motivo) => {
      try {
        await bloquear.mutateAsync(ligar ? { bloqueado: false } : { bloqueado: true, motivo });
      } catch (falha) {
        // A confirmação mostra a mensagem do erro: aqui ela já vai em texto de tela.
        throw new Error(textoDaRecusa(falha), { cause: falha });
      }
      avisos.feito(ligar ? "Convênio desbloqueado" : "Convênio bloqueado");
    },
  });

  const itens: ItemDoMenu[] = [
    ...(ehAdmin
      ? ([
          {
            tipo: "link",
            rotulo: "Editar",
            para: rotaDoConvenio(convenio.pessoa_id, convenio.id, { abrir: "editar" }),
          },
        ] as const)
      : []),
    { tipo: "link", rotulo: "Contas", para: rotaDoCedente(convenio.pessoa_id, "contas") },
    ...(ehAdmin
      ? ([{ tipo: "interruptor", rotulo: "Ativo", ligado, aoAlternar: situacao.pedir }] as const)
      : []),
  ];

  return (
    <>
      <MenuDeLinha rotulo={`Ações do convênio ${nome}`} itens={itens} />
      {situacao.dialogo}
    </>
  );
}

/*
 * Situação fica junto de Ações, como na linha do convênio dentro da conta: só a exceção
 * tem pílula, e a primeira coluna não pode ser um vão. `188px` é `Pendente de credencial`.
 * Em 1024 saem modo de registro e carteira, que o painel do convênio mostra.
 */
const COLUNAS: readonly Coluna<Convenio>[] = [
  {
    chave: "banco",
    rotulo: "Banco",
    largura: "120px",
    render: (convenio) => <span className="truncate text-texto">{banco(convenio.banco)}</span>,
  },
  {
    chave: "numero",
    rotulo: "Número",
    largura: "110px",
    render: (convenio) => <span className="dados truncate">{convenio.numero}</span>,
  },
  {
    chave: "carteira",
    rotulo: "Carteira",
    largura: "84px",
    descarte: 2,
    render: (convenio) => (
      <span className="dados text-texto-2">
        {convenio.carteira}
        {convenio.variacao ? `/${convenio.variacao}` : ""}
      </span>
    ),
  },
  {
    chave: "cedente",
    rotulo: "Cedente",
    largura: "minmax(180px, 1fr)",
    render: (convenio) => (
      <span className="truncate text-texto-2">{convenio.pessoa_nome ?? "—"}</span>
    ),
  },
  {
    chave: "modo",
    rotulo: "Registro",
    largura: "124px",
    descarte: 1,
    render: (convenio) => {
      const modo = convenio.modo_registro;
      return (
        <span className="truncate text-texto-2">
          {modo ? (MODOS_DE_REGISTRO[modo] ?? modo) : "—"}
        </span>
      );
    },
  },
  {
    chave: "situacao",
    rotulo: "Situação",
    largura: "188px",
    direita: true,
    render: (convenio) => {
      const situacao = SITUACOES[convenio.status];
      return (
        <PilulaDeExcecao
          normal={convenio.status === "ativo"}
          matiz={situacao?.matiz ?? "neutro"}
        >
          {situacao?.rotulo ?? convenio.status}
        </PilulaDeExcecao>
      );
    },
  },
  {
    chave: "acoes",
    rotulo: "Ações",
    largura: "56px",
    direita: true,
    interativa: true,
    render: (convenio) => <AcoesDoConvenio convenio={convenio} />,
  },
];
